(() => {
  const STORAGE_KEYS = {
    submitted: "waitlist_submitted",
    submittedAt: "waitlist_submitted_at",
    emailHash: "waitlist_email_hash",
    anonymousId: "waitlist_anonymous_id"
  };

  const STEP_INDEX = {
    name: 0,
    email: 1,
    age: 2,
    consent: 3,
    review: 4
  };


  class WaitlistModal {
    constructor(root) {
      this.root = root;
      this.klaviyoKey = root.dataset.klaviyoKey;
      this.klaviyoListId = root.dataset.klaviyoList;

      this.modal = root.querySelector("[data-waitlist-modal]");
      this.dialog = root.querySelector("[data-waitlist-dialog]");
      this.overlay = root.querySelector("[data-waitlist-overlay]");
      this.form = root.querySelector("[data-waitlist-form]");
      this.steps = Array.from(root.querySelectorAll("[data-step]"));

      this.progressStep = root.querySelector("[data-waitlist-progress-step]");
      this.progressTotal = root.querySelector("[data-waitlist-progress-total]");
      this.progressFill = root.querySelector("[data-waitlist-progress-fill]");

      this.submitButton = root.querySelector("[data-waitlist-submit]");
      this.submitLabel = root.querySelector("[data-submit-label]");
      this.submitLoading = root.querySelector("[data-submit-loading]");
      this.submitError = root.querySelector("[data-waitlist-submit-error]");

      this.statePanels = {
        form: root.querySelector('[data-waitlist-state="form"]'),
        success: root.querySelector('[data-waitlist-state="success"]'),
        already: root.querySelector('[data-waitlist-state="already"]')
      };

      this.reviewFields = {
        first_name: root.querySelector("[data-review-first_name]"),
        last_name: root.querySelector("[data-review-last_name]"),
        email: root.querySelector("[data-review-email]"),
        age_bracket: root.querySelector("[data-review-age_bracket]"),
        marketing_opt_in: root.querySelector("[data-review-marketing_opt_in]")
      };

      this.currentStep = 0;
      this.isOpen = false;
      this.isSubmitting = false;
      this.lastFocusedElement = null;
      this.scrollY = 0;

      this.handleKeydown = this.handleKeydown.bind(this);

    }

    init() {
      if (!this.modal || !this.dialog || !this.form || this.steps.length === 0) return;

      if (this.progressTotal) this.progressTotal.textContent = String(this.steps.length);
      this.showStep(0, true);

      this.bindTriggers();
      this.bindModalControls();
      this.bindFormControls();
    }

    bindTriggers() {
      document.addEventListener("click", (event) => {
        const trigger = event.target.closest("[data-waitlist-open]");
        if (!trigger) return;

        if (event.defaultPrevented) return;
        if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

        event.preventDefault();
        this.open(trigger);
      });
    }

    bindModalControls() {
      if (this.overlay) {
        this.overlay.addEventListener("click", (event) => {
          event.preventDefault();
          event.stopPropagation();
        });
      }


      this.root.querySelectorAll("[data-waitlist-close]").forEach((button) => {
        button.addEventListener("click", () => this.close());
      });

      const resetButton = this.root.querySelector("[data-waitlist-reset]");
      if (resetButton) {
        resetButton.addEventListener("click", () => {
          this.clearSubmissionMemory();
          this.resetForm(true);
          this.showState("form");
          this.focusCurrentStep();
        });
      }
    }

    bindFormControls() {
      this.form.addEventListener("click", (event) => {
        const nextButton = event.target.closest("[data-next]");
        if (nextButton) {
          event.preventDefault();
          this.goToNextStep();
          return;
        }

        const backButton = event.target.closest("[data-back]");
        if (backButton) {
          event.preventDefault();
          this.goToPreviousStep();
          return;
        }

        const editButton = event.target.closest("[data-edit-step]");
        if (editButton) {
          event.preventDefault();
          const stepIndex = Number(editButton.dataset.editStep);
          if (!Number.isNaN(stepIndex)) this.showStep(stepIndex);
        }
      });

      this.form.addEventListener("submit", async (event) => {
        event.preventDefault();
        await this.submit();
      });

      this.form.addEventListener("input", (event) => {
        const fieldName = event.target && event.target.name;
        if (fieldName) this.clearFieldError(fieldName);
      });

      this.form.addEventListener("change", () => {
        if (this.currentStep === STEP_INDEX.review) this.renderReview();
      });
    }

    open(trigger) {
      if (this.isOpen) return;

      this.lastFocusedElement = trigger || document.activeElement;

      if (this.isAlreadySubmitted()) {
        this.showState("already");
      } else {
        this.resetForm(true);
        this.showState("form");
      }

      this.modal.hidden = false;
      requestAnimationFrame(() => this.modal.classList.add("is-open"));

      this.lockScroll(true);
      document.addEventListener("keydown", this.handleKeydown);

      this.isOpen = true;
      this.focusInitial();
    }

    close() {
      if (!this.isOpen) return;

      this.modal.classList.remove("is-open");
      this.lockScroll(false);
      document.removeEventListener("keydown", this.handleKeydown);

      this.isOpen = false;
      window.setTimeout(() => {
        if (!this.isOpen) this.modal.hidden = true;
      }, 200);

      if (this.lastFocusedElement && typeof this.lastFocusedElement.focus === "function") {
        this.lastFocusedElement.focus();
      }
    }

    handleKeydown(event) {
      if (!this.isOpen) return;

      if (event.key === "Escape") {
        event.preventDefault();
        return;
      }


      if (event.key === "Tab") this.trapFocus(event);
    }

    trapFocus(event) {
      const activePanel = this.getActivePanel();
      if (!activePanel) return;

      const focusable = this.getFocusableElements(activePanel);
      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const current = document.activeElement;

      if (event.shiftKey && current === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && current === last) {
        event.preventDefault();
        first.focus();
      }
    }

    getFocusableElements(container) {
      return Array.from(
        container.querySelectorAll(
          'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        )
      ).filter((el) => {
        if (el.hasAttribute("hidden")) return false;
        return el.offsetParent !== null || el === document.activeElement;
      });
    }

    getActivePanel() {
      return Object.values(this.statePanels).find((panel) => panel && !panel.hidden) || null;
    }

    showState(stateName) {
      Object.entries(this.statePanels).forEach(([name, panel]) => {
        if (!panel) return;
        panel.hidden = name !== stateName;
      });

      if (stateName === "form") {
        this.clearSubmitError();
        this.showStep(this.currentStep, true);
      }
    }

    showStep(index, skipFocus) {
      const bounded = Math.max(0, Math.min(index, this.steps.length - 1));

      this.steps.forEach((step, i) => {
        const active = i === bounded;
        step.classList.toggle("is-active", active);
        step.hidden = !active;
      });

      this.currentStep = bounded;
      this.updateProgress();

      if (this.currentStep === STEP_INDEX.review) this.renderReview();
      if (!skipFocus) this.focusCurrentStep();
    }

    updateProgress() {
      const current = this.currentStep + 1;
      const total = this.steps.length;
      const width = (current / total) * 100;

      if (this.progressStep) this.progressStep.textContent = String(current);
      if (this.progressFill) this.progressFill.style.width = `${width}%`;
    }

    focusInitial() {
      if (!this.statePanels.form.hidden) {
        this.focusCurrentStep();
        return;
      }
      const activePanel = this.getActivePanel();
      if (!activePanel) return;
      const first = this.getFocusableElements(activePanel)[0];
      if (first) first.focus();
    }

    focusCurrentStep() {
      const current = this.steps[this.currentStep];
      if (!current) return;
      const target = this.getFocusableElements(current)[0];
      if (target) target.focus();
      else this.dialog.focus();
    }

    goToNextStep() {
      if (!this.validateStep(this.currentStep)) return;
      this.showStep(this.currentStep + 1);
    }

    goToPreviousStep() {
      this.showStep(this.currentStep - 1);
    }

    validateStep(stepIndex) {
      if (stepIndex === STEP_INDEX.name) {
        if (!this.validateTextField("first_name", "First name is required.")) return false;
        if (!this.validateTextField("last_name", "Last name is required.")) return false;
        return true;
      }
      if (stepIndex === STEP_INDEX.email) return this.validateEmailField();
      if (stepIndex === STEP_INDEX.age) return this.validateAgeBracket();
      return true;
    }


    validateTextField(fieldName, message) {
      const input = this.form.elements[fieldName];
      if (!input) return true;

      const value = String(input.value || "").trim();
      if (!value) {
        this.setFieldError(fieldName, message);
        input.focus();
        return false;
      }

      this.clearFieldError(fieldName);
      return true;
    }

    validateEmailField() {
      const input = this.form.elements.email;
      if (!input) return true;

      const value = String(input.value || "").trim();
      if (!value) {
        this.setFieldError("email", "Email address is required.");
        input.focus();
        return false;
      }

      if (!input.checkValidity()) {
        this.setFieldError("email", "Enter a valid email address.");
        input.focus();
        return false;
      }

      this.clearFieldError("email");
      return true;
    }

    validateAgeBracket() {
      const selected = this.form.querySelector('input[name="age_bracket"]:checked');
      const firstRadio = this.form.querySelector('input[name="age_bracket"]');

      if (!selected) {
        this.setFieldError("age_bracket", "Select an age bracket.");
        if (firstRadio) firstRadio.focus();
        return false;
      }

      this.clearFieldError("age_bracket");
      return true;
    }

    setFieldError(fieldName, message) {
      const errorEl = this.root.querySelector(`[data-error-for="${fieldName}"]`);
      if (errorEl) errorEl.textContent = message;

      if (fieldName === "age_bracket") {
        this.form.querySelectorAll('input[name="age_bracket"]').forEach((radio) => {
          radio.setAttribute("aria-invalid", "true");
        });
        return;
      }

      const field = this.form.elements[fieldName];
      if (field && typeof field.setAttribute === "function") {
        field.setAttribute("aria-invalid", "true");
      }
    }

    clearFieldError(fieldName) {
      const errorEl = this.root.querySelector(`[data-error-for="${fieldName}"]`);
      if (errorEl) errorEl.textContent = "";

      if (fieldName === "age_bracket") {
        this.form.querySelectorAll('input[name="age_bracket"]').forEach((radio) => {
          radio.removeAttribute("aria-invalid");
        });
        return;
      }

      const field = this.form.elements[fieldName];
      if (field && typeof field.removeAttribute === "function") {
        field.removeAttribute("aria-invalid");
      }
    }

    validateRequiredSteps() {
      const required = [STEP_INDEX.name, STEP_INDEX.email, STEP_INDEX.age];
      for (let i = 0; i < required.length; i += 1) {
        const idx = required[i];
        if (!this.validateStep(idx)) {
          this.showStep(idx);
          return false;
        }
      }
      return true;
    }


    getFormValues() {
      const data = new FormData(this.form);
      return {
        first_name: String(data.get("first_name") || "").trim(),
        last_name: String(data.get("last_name") || "").trim(),
        email: String(data.get("email") || "").trim().toLowerCase(),
        age_bracket: String(data.get("age_bracket") || "").trim(),
        marketing_opt_in: data.get("marketing_opt_in") === "true"
      };
    }

    renderReview() {
      const values = this.getFormValues();

      if (this.reviewFields.first_name) this.reviewFields.first_name.textContent = values.first_name || "—";
      if (this.reviewFields.last_name) this.reviewFields.last_name.textContent = values.last_name || "—";
      if (this.reviewFields.email) this.reviewFields.email.textContent = values.email || "—";
      if (this.reviewFields.age_bracket) this.reviewFields.age_bracket.textContent = values.age_bracket || "—";
      if (this.reviewFields.marketing_opt_in) this.reviewFields.marketing_opt_in.textContent = values.marketing_opt_in ? "Yes" : "No";
    }

async submit() {
      if (this.isSubmitting) return;
      if (!this.validateRequiredSteps()) return;

      if (!this.klaviyoKey || !this.klaviyoListId) {
        this.showSubmitError("Configuration error: Klaviyo API Key or List ID is missing. Please add them in the theme editor.");
        return;
      }

      this.clearSubmitError();
      this.setSubmitting(true);

      const values = this.getFormValues();

      const payload = {
        data: {
          type: "subscription",
          attributes: {
            custom_source: "Waitlist Modal",
            profile: {
              data: {
                type: "profile",
                attributes: {
                  email: values.email,
                  first_name: values.first_name,
                  last_name: values.last_name,
                  properties: {
                    Waitlist_Age_Bracket: values.age_bracket,
                    Marketing_Opt_In: values.marketing_opt_in,
                    Waitlist_Source_URL: window.location.href,
                    Anonymous_Browser_ID: this.getAnonymousId()
                  }
                }
              }
            }
          },
          relationships: {
            list: {
              data: {
                type: "list",
                id: this.klaviyoListId
              }
            }
          }
        }
      };

      try {
        const response = await fetch(`https://a.klaviyo.com/client/subscriptions/?company_id=${this.klaviyoKey}`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "revision": "2024-02-15"
          },
          body: JSON.stringify(payload)
        });

        if (!response.ok) {
          let errorMessage = "We could not submit the form. Please try again.";
          try {
            const errorJson = await response.json();
            if (errorJson.errors && errorJson.errors.length > 0) {
              errorMessage = errorJson.errors[0].detail || errorMessage;
            }
          } catch (e) {}
          throw new Error(errorMessage);
        }

        await this.persistSubmission(values.email);
        this.showState("success");

        const closeButton = this.statePanels.success.querySelector("[data-waitlist-close]");
        if (closeButton) closeButton.focus();
      } catch (error) {
        this.showSubmitError(error && error.message ? error.message : "Something went wrong. Please try again.");
      } finally {
        this.setSubmitting(false);
      }
    }
    setSubmitting(isSubmitting) {
      this.isSubmitting = isSubmitting;

      this.root.querySelectorAll("[data-next], [data-back], [data-edit-step], [data-waitlist-submit]").forEach((button) => {
        button.disabled = isSubmitting;
      });

      if (this.submitLabel) this.submitLabel.hidden = isSubmitting;
      if (this.submitLoading) this.submitLoading.hidden = !isSubmitting;
    }

    showSubmitError(message) {
      if (!this.submitError) return;
      this.submitError.textContent = message;
      this.submitError.hidden = false;
    }

    clearSubmitError() {
      if (!this.submitError) return;
      this.submitError.textContent = "";
      this.submitError.hidden = true;
    }

    resetForm(clearValues) {
      if (clearValues) this.form.reset();

      this.root.querySelectorAll("[data-error-for]").forEach((el) => {
        el.textContent = "";
      });

      this.clearSubmitError();
      this.setSubmitting(false);
      this.currentStep = 0;
      this.showStep(0, true);
      this.renderReview();
    }

    lockScroll(locked) {
      if (locked) {
        this.scrollY = window.scrollY || window.pageYOffset || 0;

        document.documentElement.classList.add("waitlist-modal-open");
        document.body.classList.add("waitlist-modal-open");

        document.body.style.position = "fixed";
        document.body.style.top = `-${this.scrollY}px`;
        document.body.style.left = "0";
        document.body.style.right = "0";
        document.body.style.width = "100%";
        return;
      }

      document.documentElement.classList.remove("waitlist-modal-open");
      document.body.classList.remove("waitlist-modal-open");

      const savedY = Math.abs(parseInt(document.body.style.top || "0", 10)) || this.scrollY || 0;

      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.left = "";
      document.body.style.right = "";
      document.body.style.width = "";

      window.scrollTo(0, savedY);
    }


    isAlreadySubmitted() {
      return this.storageGet(STORAGE_KEYS.submitted) === "true";
    }

    clearSubmissionMemory() {
      this.storageRemove(STORAGE_KEYS.submitted);
      this.storageRemove(STORAGE_KEYS.submittedAt);
      this.storageRemove(STORAGE_KEYS.emailHash);
    }

    getAnonymousId() {
      const existing = this.storageGet(STORAGE_KEYS.anonymousId);
      if (existing) return existing;

      let id = "";
      if (window.crypto && typeof window.crypto.randomUUID === "function") {
        id = window.crypto.randomUUID();
      } else {
        id = `wl-${Date.now()}-${Math.random().toString(16).slice(2, 10)}`;
      }

      this.storageSet(STORAGE_KEYS.anonymousId, id);
      return id;
    }

    async persistSubmission(email) {
      const submittedAt = new Date().toISOString();

      this.storageSet(STORAGE_KEYS.submitted, "true");
      this.storageSet(STORAGE_KEYS.submittedAt, submittedAt);

      const hash = await this.sha256Hex(email);
      if (hash) this.storageSet(STORAGE_KEYS.emailHash, hash);
    }

    async sha256Hex(value) {
      if (!window.crypto || !window.crypto.subtle) return "";

      const normalized = String(value || "").trim().toLowerCase();
      if (!normalized) return "";

      const bytes = new TextEncoder().encode(normalized);
      const hashBuffer = await window.crypto.subtle.digest("SHA-256", bytes);

      return Array.from(new Uint8Array(hashBuffer))
        .map((b) => b.toString(16).padStart(2, "0"))
        .join("");
    }

    storageGet(key) {
      try {
        return window.localStorage.getItem(key);
      } catch (_error) {
        return null;
      }
    }

    storageSet(key, value) {
      try {
        window.localStorage.setItem(key, value);
      } catch (_error) {
        // Ignore localStorage write failures.
      }
    }

    storageRemove(key) {
      try {
        window.localStorage.removeItem(key);
      } catch (_error) {
        // Ignore localStorage remove failures.
      }
    }
  }

  function initWaitlistModal() {
    if (window.__waitlistModalInstance) return;

    const root = document.querySelector("[data-waitlist-root]");
    if (!root) return;

    const instance = new WaitlistModal(root);
    instance.init();
    window.__waitlistModalInstance = instance;
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initWaitlistModal);
  } else {
    initWaitlistModal();
  }
})();
